import { useState } from "react";
import { Link } from "react-router-dom";
import { Video } from "lucide-react";
import { SidebarMenuItem, SidebarMenuButton } from "@/components/ui/sidebar";
import { MenuTutorialVideoModal } from "./MenuTutorialVideoModal";
import { useMenuTutorialVideos } from "@/hooks/useMenuTutorialVideos";

interface MainMenuItemWithTutorialProps {
  id: string;
  label: string;
  path: string;
  icon?: React.ComponentType<{ className?: string }> | string;
  isActive: boolean;
  level?: number;
  tutorialVideo?: { videoUrl: string; title: string; lessonId?: string };
  additionalIcons?: React.ReactNode; // New prop for additional icons (like PIN shield)
}

export function MainMenuItemWithTutorial({
  id,
  label,
  path,
  icon: Icon,
  isActive,
  level = 0,
  tutorialVideo,
  additionalIcons,
}: MainMenuItemWithTutorialProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showTutorialIcons, isWatched, markAsWatched } = useMenuTutorialVideos();

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleWatched = () => {
    markAsWatched.mutate({ menuItemId: id, sidebarType: 'main' });
  };

  // Helper to render icon (handles both Lucide components and image paths)
  const renderIcon = () => {
    if (!Icon) return null;
    // If Icon is a string (image path), render as img
    if (typeof Icon === 'string') {
      return <img src={Icon} alt="" className="h-4 w-4 object-contain" />;
    }
    // Otherwise render as Lucide component
    return <Icon className="h-4 w-4 text-primary" />;
  };

  // Determine if we should show the video icon
  const hasVideo = !!tutorialVideo;
  const alreadyWatched = isWatched(id, 'main');
  const shouldShowIcon = hasVideo && showTutorialIcons && !alreadyWatched;

  // Generate proper padding class based on level for consistent tree visualization
  // Each level adds 20px (pl-5 = 1.25rem = 20px) for clear parent-child relationships
  const getPaddingClass = () => {
    switch (level) {
      case 0: return '';
      case 1: return 'pl-5';
      case 2: return 'pl-10';
      case 3: return 'pl-[60px]';
      default: return 'pl-20';
    }
  };

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton 
          asChild 
          isActive={isActive}
          tooltip={label}
        >
          <Link to={path} className={`group ${getPaddingClass()}`}>
            {renderIcon()}
            <span className="flex-1">{label}</span>
            <div className="flex items-center gap-1 ml-auto">
              {additionalIcons}
              {shouldShowIcon && (
                <button
                  onClick={handlePlayClick}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-primary/10 rounded"
                  title="Watch tutorial"
                >
                  <Video className="h-3 w-3 text-primary" />
                </button>
              )}
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>

      {tutorialVideo && (
        <MenuTutorialVideoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          videoUrl={tutorialVideo.videoUrl}
          title={tutorialVideo.title}
          lessonId={tutorialVideo.lessonId}
          menuItemLabel={label}
          onWatched={handleWatched}
        />
      )}
    </>
  );
}
